<template>
  <div class="w-full mt-4 md:mt-6 lg:mt-8 space-y-6 md:space-y-8">

    <!-- ── Skeleton: shown while initial data loads ── -->
    <template v-if="!finishedLoadingInitialData">

      <!-- Skeleton: Announcements -->
      <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-5 animate-pulse">
        <div class="tw-flex items-center justify-between mb-4">
          <div class="h-4 w-36 bg-gray-200 rounded"/>
          <div class="h-3 w-12 bg-gray-200 rounded"/>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div v-for="i in 2" :key="i" class="rounded-xl border border-gray-100 overflow-hidden">
            <div class="h-32 bg-gray-200"/>
            <div class="p-4 space-y-2">
              <div class="h-3 w-3/4 bg-gray-200 rounded"/>
              <div class="h-3 w-1/2 bg-gray-200 rounded"/>
              <div class="mt-3 pt-3 border-t border-gray-100 tw-flex gap-4">
                <div class="h-3 w-16 bg-gray-200 rounded"/>
                <div class="h-3 w-16 bg-gray-200 rounded"/>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Skeleton: Messages -->
      <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-5 animate-pulse">
        <div class="tw-flex items-center justify-between mb-4">
          <div class="h-4 w-32 bg-gray-200 rounded"/>
          <div class="h-3 w-12 bg-gray-200 rounded"/>
        </div>
        <div class="divide-y divide-gray-100">
          <div v-for="i in 3" :key="i" class="tw-flex items-center gap-3 py-3 px-2">
            <div class="w-8 h-8 rounded-full bg-gray-200 shrink-0"/>
            <div class="flex-1">
              <div class="tw-flex justify-between mb-1.5">
                <div class="h-3 w-24 bg-gray-200 rounded"/>
                <div class="h-3 w-10 bg-gray-200 rounded"/>
              </div>
              <div class="h-2.5 w-3/4 bg-gray-200 rounded"/>
            </div>
          </div>
        </div>
      </div>

      <!-- Skeleton: Communities -->
      <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-5 animate-pulse" v-if="this.$store.getters.hasAccessToCommunities">
        <div class="tw-flex items-center justify-between mb-4">
          <div class="h-4 w-28 bg-gray-200 rounded"/>
          <div class="h-3 w-12 bg-gray-200 rounded"/>
        </div>
        <div class="grid gap-3" style="grid-template-columns: repeat(auto-fill, minmax(180px, 1fr))">
          <div v-for="i in 3" :key="i" class="rounded-[10px] border border-gray-100 overflow-hidden">
            <div class="h-[120px] bg-gray-200"/>
            <div class="p-3 space-y-2">
              <div class="h-3 w-3/4 bg-gray-200 rounded"/>
              <div class="h-2.5 w-full bg-gray-200 rounded"/>
              <div class="h-2.5 w-2/3 bg-gray-200 rounded"/>
            </div>
          </div>
        </div>
      </div>

    </template>

    <!-- ── Early Access Banner (Foodleap, active trial, no subscription, dismissible) ── -->
    <div
      v-if="$store.getters.isFoodleap && hasActiveTransitionReportTrial && !hasActiveTransitionReportSubscription && !earlyAccessBannerDismissed && !$store.getters.hasTransitionReportPaidAccess"
      class="ph-early-access-banner relative rounded-2xl overflow-hidden"
    >
      <!-- Dismiss -->
      <button
        @click="dismissEarlyAccessBanner"
        class="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors z-10"
        :aria-label="$t('dismiss')"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>

      <div class="p-6 sm:p-8 pr-10">
        <div class="tw-flex flex-col lg:flex-row gap-8">

          <!-- Left: main content -->
          <div class="flex-1">
            <span class="ph-ea-badge">{{ $t('trp_ea_badge') }}</span>
            <h2 class="text-xl font-bold mt-4 mb-3">{{ $t('trp_ea_title') }}</h2>
            <p class="text-sm text-slate-600 mb-4">{{ $t('trp_ea_intro') }}</p>
            <div class="tw-flex flex-wrap gap-x-6 gap-y-2 mb-6 text-sm font-semibold">
              <div class="tw-flex items-center gap-2">
                <svg class="w-5 h-5 text-green-600 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path clip-rule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        fill-rule="evenodd"/>
                </svg>
                {{ $t('trp_entry_pulse_title') }}
              </div>
              <div class="tw-flex items-center gap-2">
                <svg class="w-5 h-5 text-green-600 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path clip-rule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        fill-rule="evenodd"/>
                </svg>
                {{ $t('trp_entry_radar_title') }}
              </div>
              <div class="tw-flex items-center gap-2">
                <svg class="w-5 h-5 text-green-600 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path clip-rule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        fill-rule="evenodd"/>
                </svg>
                {{ $t('trp_entry_deepdive_title') }}
              </div>
            </div>
            <p class="text-sm text-slate-700">
              {{ $t('trp_ea_until') }} <span class="font-bold text-green-700">{{ $store.getters.hasActiveTransitionReportTrialUntil }}.</span>
            </p>
          </div>

          <!-- Right: help box -->
          <div class="lg:w-64 shrink-0">
            <div class="bg-white rounded-xl border border-green-100 shadow-sm p-5">
              <div class="tw-flex gap-3 mb-4">
                <div class="w-10 h-10 rounded-lg bg-green-50 tw-flex items-center justify-center shrink-0 text-green-700">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
                  </svg>
                </div>
                <div>
                  <h4 class="font-bold text-sm text-slate-900">{{ $t('trp_ea_help_title') }}</h4>
                  <p class="text-xs text-slate-500 mt-1">{{ $t('trp_ea_help_text') }}</p>
                </div>
              </div>
              <a
                href="mailto:foodleap@datascouts.eu"
                class="no-underline ph-ea-btn-feedback w-full py-2 rounded-lg text-sm font-semibold tw-flex items-center justify-center gap-2 mb-2 transition-colors"
              >
                {{ $t('trp_ea_share_feedback') }}
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                </svg>
              </a>
            </div>
          </div>

        </div>
      </div>

      <!-- Bottom bar -->
      <div class="ph-ea-bottom-bar border-t border-green-100 px-6 sm:px-8 py-3 tw-flex items-center gap-4 text-xs font-medium text-slate-600">
        <div class="tw-flex items-center gap-2">
          <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.921-.755 1.688-1.54 1.118l-3.976-2.888a1 1 0 00-1.175 0l-3.976 2.888c-.784.57-1.838-.197-1.539-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/>
          </svg>
          {{ $t('trp_ea_founding_preview') }}
        </div>
        <div class="w-px h-3 bg-slate-300 shrink-0"></div>
        <p class="m-0">{{ $t('trp_ea_bottom_text') }}</p>
      </div>
    </div>

    <!-- ── Founding Member CTA Banner ── -->
    <div
      v-if="$store.getters.isFoodleap && !hasActiveTransitionReportSubscription && !hasActiveTransitionReportTrial && !$store.getters.hasTransitionReportPaidAccess"
      class="ph-founding-cta-banner rounded-2xl overflow-hidden"
    >
      <div class="tw-flex flex-col lg:flex-row">

        <!-- Left: content -->
        <div class="flex-1 p-8 lg:p-10">
          <span class="ph-fcta-badge">{{ $t('trp_fcta_badge') }}</span>
          <h2 class="text-xl font-bold text-slate-900 mt-5 mb-3 leading-tight">
            {{ $t('trp_fcta_title') }}
          </h2>
          <p class="text-sm text-slate-600 mb-6">
            {{ $t('trp_fcta_subtitle') }}
          </p>

          <div class="tw-flex flex-col gap-3 mb-8">
            <div
              v-for="feature in [$t('trp_fcta_feature_pulse'), $t('trp_fcta_feature_radar'), $t('trp_fcta_feature_deepdive')]"
              :key="feature" class="tw-flex items-center gap-3 text-sm text-slate-700 font-medium">
              <svg class="w-5 h-5 shrink-0 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path clip-rule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      fill-rule="evenodd"/>
              </svg>
              {{ feature }}
            </div>
          </div>

          <div class="tw-flex flex-wrap items-center gap-3">
            <button
              @click="$router.push('/transition-reports/trial')"
              class="ph-fcta-btn-primary px-6 py-3 rounded-xl text-sm font-bold tw-flex items-center gap-2 transition-colors"
            >
              {{ $t('trp_subscribe_now') }}
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
              </svg>
            </button>
            <a
              href="mailto:foodleap@datascouts.eu"
              class="ph-fcta-btn-secondary no-underline px-6 py-3 rounded-xl text-sm font-semibold transition-colors"
            >
              {{ $t('trp_contact_us') }}
            </a>
          </div>
        </div>

        <!-- Right: price -->
        <div class="ph-fcta-right lg:w-60 shrink-0 p-8 lg:p-10 tw-flex flex-col justify-center items-center text-center">
          <svg class="w-10 h-10 text-green-700 opacity-60 mb-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
          </svg>
          <p class="text-xs text-slate-500 uppercase tracking-widest font-semibold mb-2">{{ $t('trp_founding_member') }}</p>
          <p class="text-4xl font-extrabold text-slate-900 mb-1">€9,000</p>
          <p class="text-sm text-slate-500 mb-5">{{ $t('trp_per_year') }}</p>
          <p class="text-xs text-slate-400 leading-relaxed">{{ $t('trp_fcta_early_pricing') }}</p>
        </div>

      </div>
    </div>

    <!-- T025: Subscription success banner (shown once after returning from Stripe Checkout) -->
    <div
      v-if="$store.getters.isFoodleap && subscriptionSuccess"
      class="bg-green-50 border border-green-200 rounded-xl p-5 tw-flex items-center justify-between gap-4"
    >
      <div>
        <p class="text-sm font-semibold text-green-800 m-0 mb-0.5">{{ $t('trp_sub_success_title') }}</p>
        <p class="text-sm text-green-700 m-0">{{ $t('trp_sub_success_text') }}</p>
      </div>
      <button
        @click="subscriptionSuccess = false"
        class="shrink-0 text-green-600 hover:text-green-800 transition-colors"
        :aria-label="$t('dismiss')"
      >
        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>

    <!-- ── TRP Entry Cards (Pulse / Radar / Deep Dive) ── -->
    <div
      v-if="((isUserOfDataScoutsTeam || canAccessReports || hasActiveTransitionReportTrial) && $store.getters.isFoodleap) || $store.getters.isStartupEcosystem"
      class="grid gap-4 md:grid-cols-3"
    >
      <!-- Pulse card -->
      <div
        class="bg-white rounded-xl border border-gray-100 shadow-sm p-6 cursor-pointer hover:shadow-md transition-shadow tw-flex flex-col justify-between overflow-hidden relative"
        @click="transitionPulseMonths.length > 0 ? $router.push(`/transition-pulse/${transitionPulseMonths[0].id}`) : $router.push('/transition-pulse')"
      >
        <div>
          <div class="w-12 h-12 rounded-full tw-flex items-center justify-center mb-4" style="background-color: rgba(255,200,61,0.15)">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: #ffc83d">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
            </svg>
          </div>
          <h4 class="font-bold text-base text-gray-900 m-0 mb-1">{{ $t('trp_entry_pulse_title') }}</h4>
          <p class="text-xs font-bold m-0 mb-3" style="color: #ffc83d">{{ $t('trp_entry_pulse_tagline') }}</p>
          <p class="text-xs text-gray-500 leading-relaxed m-0">{{ $t('trp_entry_pulse_desc') }}</p>
        </div>
        <div class="tw-flex items-center justify-between mt-8">
          <span class="text-xs font-bold tw-flex items-center gap-1" style="color: #ffc83d">
            {{ $t('trp_entry_pulse_view') }}
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
            </svg>
          </span>
          <svg class="ph-sparkline w-24 h-12" viewBox="0 0 100 40" style="color: #ffc83d">
            <path class="ph-spark-path" d="M0,35 Q10,35 20,30 T40,25 T60,35 T80,15 T100,20" fill="none" stroke="currentColor" stroke-width="2"/>
          </svg>
        </div>
      </div>

      <!-- Radar card -->
      <div
        class="bg-white rounded-xl border border-gray-100 shadow-sm p-6 cursor-pointer hover:shadow-md transition-shadow tw-flex flex-col justify-between overflow-hidden relative"
        @click="openInnovationRadar"
      >
        <div>
          <div class="w-12 h-12 rounded-full tw-flex items-center justify-center mb-4" style="background-color: rgba(92,204,97,0.15)">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: #5ccc61">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
            </svg>
          </div>
          <h4 class="font-bold text-base text-gray-900 m-0 mb-1">{{ $t('trp_entry_radar_title') }}</h4>
          <p class="text-xs font-bold m-0 mb-3" style="color: #5ccc61">{{ $t('trp_entry_radar_tagline') }}</p>
          <p class="text-xs text-gray-500 leading-relaxed m-0">{{ $t('trp_entry_radar_desc') }}</p>
        </div>
        <div class="tw-flex items-center justify-between mt-8">
          <span class="text-xs font-bold tw-flex items-center gap-1" style="color: #5ccc61">
            {{ $t('trp_entry_radar_view') }}
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
            </svg>
          </span>
          <div class="ph-radar-mini relative w-12 h-12 rounded-full overflow-hidden">
            <div class="ph-radar-sweep absolute inset-0 rounded-full"></div>
            <div class="absolute inset-0 rounded-full" style="border: 1px solid rgba(92,204,97,0.3)"></div>
            <div class="absolute inset-2 rounded-full" style="border: 1px solid rgba(92,204,97,0.3)"></div>
            <div class="absolute inset-4 rounded-full" style="border: 1px solid rgba(92,204,97,0.3)"></div>
            <div class="ph-radar-dot" style="top:8px; right:9px;"></div>
            <div class="ph-radar-dot ph-radar-dot--2" style="top:20px; left:2px;"></div>
            <div class="ph-radar-dot ph-radar-dot--3" style="bottom:4px; right:12px;"></div>
          </div>
        </div>
      </div>

      <!-- Deep Dive Reports card -->
      <div
        v-if="(isUserOfDataScoutsTeam || canAccessReports || hasActiveTransitionReportTrial) && $store.getters.isFoodleap"
        class="bg-white rounded-xl border border-gray-100 shadow-sm p-6 cursor-pointer hover:shadow-md transition-shadow tw-flex flex-col justify-between overflow-hidden relative"
        @click="openDeepDiveReport"
      >
        <div>
          <div class="w-12 h-12 rounded-full tw-flex items-center justify-center mb-4" style="background-color: rgba(112,193,255,0.15)">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: #70c1ff">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5s3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
            </svg>
          </div>
          <h4 class="font-bold text-base text-gray-900 m-0 mb-1">{{ $t('trp_entry_deepdive_title') }}</h4>
          <p class="text-xs font-bold m-0 mb-3" style="color: #70c1ff">{{ $t('trp_entry_deepdive_tagline') }}</p>
          <p class="text-xs text-gray-500 leading-relaxed m-0">{{ $t('trp_entry_deepdive_desc') }}</p>
        </div>
        <div class="tw-flex items-center justify-between mt-8">
          <span class="text-xs font-bold tw-flex items-center gap-1" style="color: #70c1ff">
            {{ $t('trp_entry_deepdive_view') }}
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
            </svg>
          </span>
          <div class="ph-deepdive-stack tw-flex -space-x-3">
            <div class="ph-deepdive-back w-8 h-10 rounded" style="background-color: rgba(112,193,255,0.25); border: 1px solid rgba(112,193,255,0.4)"></div>
            <div class="ph-deepdive-front w-8 h-10 rounded" style="background-color: rgba(112,193,255,0.1); border: 1px solid rgba(112,193,255,0.4)"></div>
          </div>
        </div>
      </div>
    </div>


    <!-- Announcements Section -->
    <div v-if="announcements.length > 0 && $store.getters.hasAccessToCommunities" class="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
      <div class="tw-flex items-center justify-between mb-4">
        <h3 class="text-base font-semibold text-gray-900 m-0" v-if="joinedCommunities.length > 1">
          {{ $t('personal_home_latest_announcements', { announcements: announcementsLabel.toLowerCase() }) }}
        </h3>
        <h3 class="text-base font-semibold text-gray-900 m-0" v-else>
          {{ $t('personal_home_latest_announcements', { announcements: announcementsLabel.toLowerCase(), community: joinedCommunities[0].name }) }}
        </h3>
        <router-link to="/announcements" class="tw-flex items-center gap-1 text-xs font-medium no-underline opacity-80 hover:opacity-100 transition-opacity ph-link-primary"
                     @click="trackViewAll('announcements')">
          {{ $t('view_all') }}
          <Icon name="arrow-right" size="14"/>
        </router-link>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PersonalHomeAnnouncementCard
          v-for="announcement in announcements"
          :key="announcement.id"
          :announcement="announcement"
          @open="openAnnouncementModal(announcement)"
        />
      </div>

      <PersonalHomeAnnouncementModal
        v-if="activeAnnouncement"
        v-model="announcementModalOpen"
        :announcement="activeAnnouncement"
      />
    </div>

    <!-- Recent Messages Section -->
    <div v-if="missedMessages.length > 0" class="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
      <div class="tw-flex items-center justify-between mb-4">
        <h3 class="text-base font-semibold text-gray-900 m-0">{{ $t('personal_home_recent_messages') }}</h3>
        <router-link to="/profile/conversations"
                     class="tw-flex items-center gap-1 text-xs font-medium no-underline opacity-80 hover:opacity-100 transition-opacity ph-link-primary"
                     @click="trackViewAll('messages')">
          {{ $t('view_all') }}
          <Icon name="arrow-right" size="14"/>
        </router-link>
      </div>
      <div class="divide-y divide-gray-100">
        <div
          v-for="message in missedMessages"
          :key="message.id"
          class="tw-flex items-center gap-3 py-3 px-2 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors"
          @click="openConversation(message.conversationId, message.sender.id)"
        >
          <div class="shrink-0">
            <AvatarNew :user="message.sender"/>
          </div>
          <div class="min-w-0 flex-1">
            <div class="tw-flex items-baseline justify-between gap-2 mb-0.5">
              <span class="text-sm font-semibold text-gray-900 truncate">{{ message.sender.name }}</span>
              <span class="text-xs text-gray-400 shrink-0">{{ formatMessageTime(message.sendAt) }}</span>
            </div>
            <p class="text-xs text-gray-500 truncate m-0">{{ message.message }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Community Members Section -->
    <div v-if="latestCommunityMembers.length > 0 && this.$store.getters.hasAccessToCommunities" class="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
      <div class="tw-flex items-center justify-between mb-4">
        <h3 class="text-base font-semibold text-gray-900 m-0">{{ $t('personal_home_new_community_members') }}</h3>
        <!--        <router-link to="/communities" class="tw-flex items-center gap-1 text-xs font-medium no-underline opacity-80 hover:opacity-100 transition-opacity ph-link-primary">
                  {{ $t('view_all') }}
                  <Icon name="arrow-right" size="14"/>
                </router-link>-->
      </div>
      <div class="space-y-3 md:space-y-4">
        <CommunityMemberCard
          v-for="member in latestCommunityMembers"
          :key="member.id"
          :member="member"
          :compact="false"
          :show-actions="true"
          :show-organization="true"
          :show-organization-link="true"
          :show-department="true"
          :show-experience="false"
        />
      </div>
    </div>

    <!-- ── My Communities ── -->
    <div
      v-if="$store.getters.hasAccessToCommunities && (joinedCommunities.length || suggestedCommunities.length)"
      class="bg-white rounded-xl border border-gray-100 shadow-sm p-5"
    >
      <!-- Block header -->
      <div class="tw-flex items-center justify-between mb-4">
        <h3 class="text-base font-semibold text-gray-900 m-0">{{ $t('personal_home_my_communities') }}</h3>
        <router-link to="/communities" class="tw-flex items-center gap-1 text-xs font-medium no-underline opacity-80 hover:opacity-100 transition-opacity ph-link-primary"
                     @click="trackViewAll('communities')">
          {{ $t('view_all') }}
          <Icon name="arrow-right" size="14"/>
        </router-link>
      </div>

      <!-- Joined communities -->
      <div v-if="joinedCommunities.length" class="ph-communities-rail">
        <PersonalHomeCommunityCard
          v-for="community in joinedCommunities.slice(0, 3)"
          :key="community.id"
          :community="community"
          @click="viewCommunity(community)"
        />
      </div>
      <!-- Empty: no joined communities -->
      <div v-else class="tw-flex flex-col items-center justify-center py-10 px-4 text-center ph-empty-anim">
        <div class="ph-empty-state__icon-wrap ph-empty-state__icon-wrap--pulse">
          <Icon name="users" size="large"/>
        </div>
        <p class="text-sm text-gray-500 m-0 mb-2.5 ph-empty-anim-text">{{ $t('profile_no_communities_joined') }}</p>
        <DsButton
          variant="rounded"
          to="/communities"
          :label="$t('profile_browse_communities')"
          size="small"
        />
      </div>

      <!-- Suggested divider + row -->
      <template v-if="suggestedCommunities.length">
        <div class="tw-flex items-center gap-3 my-4">
          <span class="text-[11px] font-semibold uppercase tracking-widest text-gray-400 whitespace-nowrap">{{ $t('personal_home_communities_suggested') }}</span>
          <div class="flex-1 h-px bg-gray-200"/>
        </div>

        <div class="ph-communities-rail ph-communities-rail--suggested">
          <PersonalHomeCommunityCard
            v-for="community in suggestedCommunities"
            :key="community.id"
            :community="community"
            variant="suggested"
            @click="$router.push('/communities/' + community.id)"
          />
        </div>
      </template>
    </div>

    <!-- ── My Spotting Areas ── -->
    <div
      v-if="$store.getters.hasAccessToNewSpottingAreas"
      class="bg-white rounded-xl border border-gray-100 shadow-sm p-5"
    >
      <div class="tw-flex items-center justify-between mb-4">
        <h3 class="text-base font-semibold text-gray-900 m-0">{{ spottingAreasLabel }}</h3>
        <router-link to="/spotting-areas" class="tw-flex items-center gap-1 text-xs font-medium no-underline opacity-80 hover:opacity-100 transition-opacity ph-link-primary"
                     @click="trackViewAll('spotting-areas')">
          {{ $t('view_all') }}
          <Icon name="arrow-right" size="14"/>
        </router-link>
      </div>
      <div v-if="joinedSpottingAreas.length" class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div
          v-for="area in joinedSpottingAreas.slice(0, 6)"
          :key="area.id"
          class="group cursor-pointer overflow-hidden rounded-lg border border-gray-200 transition-all hover:shadow-md"
          @click="viewSpottingArea(area)"
        >
          <div class="relative h-40 overflow-hidden bg-gray-100">
            <img
              v-if="area.image"
              :src="getImageForSpottingArea(area)"
              :alt="area.name"
              class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
            <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"/>
            <div class="absolute bottom-3 left-3 right-3">
              <h5 class="font-bold text-white text-sm uppercase tracking-wide truncate">{{ area.name }}</h5>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="tw-flex flex-col items-center justify-center py-7 px-4 text-center ph-empty-anim">
        <div class="ph-empty-state__icon-wrap ph-empty-state__icon-wrap--float">
          <Icon name="location-marker" size="large"/>
        </div>
        <p class="text-sm text-gray-500 m-0 ph-empty-anim-text">{{ $t('profile_no_spotting_areas_access') }}</p>
      </div>
    </div>

    <!-- ── My Actors ── -->
    <div
      v-if="actorClaims.length || showAddActorButton"
      id="my-actors-section"
      class="bg-white rounded-xl border border-gray-100 shadow-sm p-5"
    >
      <div class="tw-flex items-center justify-between mb-4">
        <h3 class="text-base font-semibold text-gray-900 m-0">{{ $t('profile_settings_my_actors', { actors: actorsLabel }) }}</h3>
        <DsButton
          variant="rounded"
          v-if="showAddActorButton"
          @click="showCreateActorModal"
          icon="plus"
          size="small"
          :label="$t('add_actor_add_button', { actor: actorLabel })"
        />
      </div>
      <div>
        <div v-if="!actorClaims.length" class="tw-flex flex-col items-center justify-center py-7 px-4 text-center ph-empty-anim">
          <div class="ph-empty-state__icon-wrap ph-empty-state__icon-wrap--bounce">
            <Icon name="building" size="large"/>
          </div>
          <p class="text-sm text-gray-500 m-0 mb-2.5 ph-empty-anim-text">{{ $t('personal_home_no_actors_claimed') }}</p>
          <DsButton
            v-if="showAddActorButton"
            @click="showCreateActorModal"
            variant="rounded"
            size="small"
            icon="plus"
            :label="$t('add_actor_add_button', { actor: actorLabel})"
          />
        </div>
        <div v-else class="grid gap-4 md:grid-cols-2">
          <div
            v-for="actor in actorClaims"
            :key="actor.id"
            class="overflow-hidden rounded-lg border border-gray-200 bg-white transition-shadow hover:shadow-md"
          >
            <div class="p-4">
              <router-link :to="'/actors/' + actor.id" class="tw-flex items-start gap-3">
                <div class="shrink-0">
                  <ActorAvatar :actor="actor" :width="40"/>
                </div>
                <div class="min-w-0 flex-1">
                  <h5 class="font-semibold text-gray-900 text-sm truncate mb-0.5">{{ actor.name }}</h5>
                </div>
              </router-link>
              <p v-if="actor.short_description" class="mt-3 line-clamp-2 text-sm text-gray-500 leading-relaxed">
                {{ actor.short_description }}
              </p>
            </div>
            <div class="border-t border-gray-100 bg-gray-50 px-4 py-3">
              <div class="tw-flex items-center justify-between mb-2">
                <span class="text-xs text-gray-400">{{ $t('personal_home_weekly_trend') }}</span>
                <div
                  :class="['tw-flex items-center gap-1 text-xs font-medium',
                    actorTrend(actor.id) === 'up' ? 'text-green-600' :
                    actorTrend(actor.id) === 'down' ? 'text-red-500' : 'text-gray-400']"
                >
                  <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          :d="actorTrend(actor.id) === 'down'
                        ? 'M13 17H5m0 0v-8m0 8l8-8 4 4 6-6'
                        : actorTrend(actor.id) === 'up'
                          ? 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6'
                          : 'M5 12h14'"
                    />
                  </svg>
                  {{
                    actorTrend(actor.id) === 'up' ? $t('personal_home_actor_trend_growing') : actorTrend(actor.id) === 'down' ? $t('personal_home_actor_trend_declining') : $t('personal_home_actor_trend_stable')
                  }}
                </div>
              </div>
              <div class="tw-flex items-end gap-px h-8">
                <div
                  v-for="(bar, i) in actorBarData(actor.id)"
                  :key="i"
                  class="flex-1 rounded-t bg-primary hover:bg-primary transition-colors"
                  :style="{ height: bar + '%' }"
                  v-tooltip.top="$t('personal_home_views') + ': ' + actorBarData(actor.id, true)[i]"
                />
              </div>
            </div>
          </div>
        </div><!-- end v-else grid -->
      </div>
    </div>

    <!-- ── My Products ── -->
    <div
      v-if="productClaims.length || showAddProductButton"
      id="my-products-section"
      class="bg-white rounded-xl border border-gray-100 shadow-sm p-5"
    >
      <div class="tw-flex items-center justify-between mb-4">
        <h3 class="text-base font-semibold text-gray-900 m-0">{{ $t('profile_settings_my_products', { products: productsLabel }) }}</h3>
        <DsButton
          variant="rounded"
          v-if="showAddProductButton"
          @click="showAddProductModal"
          icon="plus"
          size="small"
          :label="$t('add_product_add_button', { product: productLabel })"
        />
      </div>
      <div>
        <div v-if="!productClaims.length" class="tw-flex flex-col items-center justify-center py-7 px-4 text-center ph-empty-anim">
          <div class="ph-empty-state__icon-wrap ph-empty-state__icon-wrap--bounce">
            <Icon name="icons" size="large"/>
          </div>
          <p class="text-sm text-gray-500 m-0 mb-2.5 ph-empty-anim-text">{{ $t('profile_no_products', { actors: productsLabel }) }}</p>
          <DsButton
            v-if="showAddProductButton"
            @click="showAddProductModal"
            variant="rounded"
            size="small"
            icon="plus"
            :label="$t('add_product_add_button', {product: productLabel})"
          />
        </div>
        <div v-else class="grid gap-4 md:grid-cols-2">
          <div
            v-for="product in productClaims"
            :key="product.id"
            class="overflow-hidden rounded-lg border border-gray-200 bg-white transition-shadow hover:shadow-md"
          >
            <div class="p-4">
              <router-link :to="'/actors/' + product.id" class="tw-flex items-start gap-3">
                <div class="shrink-0">
                  <ActorAvatar :actor="product" :width="40"/>
                </div>
                <div class="min-w-0 flex-1">
                  <h5 class="font-semibold text-gray-900 text-sm truncate mb-0.5">{{ product.name }}</h5>
                </div>
              </router-link>
              <p v-if="product.short_description" class="mt-3 line-clamp-2 text-sm text-gray-500 leading-relaxed">
                {{ product.short_description }}
              </p>
            </div>
            <div class="border-t border-gray-100 bg-gray-50 px-4 py-3">
              <div class="tw-flex items-center justify-between mb-2">
                <span class="text-xs text-gray-400">{{ $t('personal_home_weekly_trend') }}</span>
                <div
                  :class="['tw-flex items-center gap-1 text-xs font-medium',
                    actorTrend(product.id) === 'up' ? 'text-green-600' :
                    actorTrend(product.id) === 'down' ? 'text-red-500' : 'text-gray-400']"
                >
                  <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          :d="actorTrend(product.id) === 'down'
                        ? 'M13 17H5m0 0v-8m0 8l8-8 4 4 6-6'
                        : actorTrend(product.id) === 'up'
                          ? 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6'
                          : 'M5 12h14'"
                    />
                  </svg>
                  {{
                    actorTrend(product.id) === 'up' ? $t('personal_home_actor_trend_growing') : actorTrend(product.id) === 'down' ? $t('personal_home_actor_trend_declining') : $t('personal_home_actor_trend_stable')
                  }}
                </div>
              </div>
              <div class="tw-flex items-end gap-px h-8">
                <div
                  v-for="(bar, i) in actorBarData(product.id)"
                  :key="i"
                  class="flex-1 rounded-t bg-primary hover:bg-primary transition-colors"
                  :style="{ height: bar + '%' }"
                  v-tooltip.top="$t('personal_home_views') + ': ' + actorBarData(product.id, true)[i]"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Testimonials ── -->
    <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
      <div class="tw-flex items-center justify-between mb-5">
        <h3 class="text-base font-semibold text-gray-900 m-0">{{ $t('testimonials_title') }}</h3>
        <ds-button
          v-if="actorClaims.length > 0"
          icon="plus"
          :label="$t('write_testimonial')"
          variant="rounded"
          size="small"
          @click="openWriteTestimonialModal"
        />
      </div>

      <!-- Empty state -->
      <div
        v-if="testimonials?.given?.length === 0 && testimonials?.received?.length === 0"
        class="tw-flex flex-col items-center justify-center py-10 text-center ph-empty-anim"
      >
        <div class="w-12 h-12 rounded-full bg-gray-100 tw-flex items-center justify-center mb-3 ph-empty-state__icon-wrap ph-empty-state__icon-wrap--bounce">
          <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
          </svg>
        </div>
        <p class="text-sm text-gray-500 m-0 ph-empty-anim-text">{{ $t('no_testimonials') }}</p>
        <ds-button
          v-if="actorClaims.length > 0"
          icon="plus"
          :label="$t('write_testimonial')"
          variant="rounded"
          size="small"
          class="mt-3"
          @click="openWriteTestimonialModal"
        />
      </div>

      <div v-else class="space-y-6">
        <!-- Sent -->
        <div v-if="testimonials?.given?.length > 0">
          <div class="tw-flex items-center gap-2 mb-4">
            <span class="text-xs font-semibold uppercase tracking-widest text-primary whitespace-nowrap">{{ $t('testimonials_sent') }}</span>
            <div class="flex-1 h-px bg-gray-100"/>
          </div>
          <div v-for="group in testimonials.given" :key="group.target_actor?.id" class="mb-5">
            <a
              :href="'/actors/' + group.target_actor?.elastic_id"
              class="inline-flex items-center gap-2 mb-3 !no-underline group"
            >
              <ActorAvatar :actor="group.target_actor" :width="24"/>
              <span class="text-xs font-semibold text-gray-600 group-hover:text-primary transition-colors">{{ group.target_actor?.name }}</span>
            </a>
            <div class="grid md:grid-cols-2 gap-3">
              <div
                v-for="testimonial in group.testimonials"
                :key="testimonial.id"
                class="relative bg-gray-50 rounded-xl p-4 border border-gray-100 tw-flex flex-col"
              >
                <svg class="absolute top-3 right-3 h-5 w-5 text-gray-200" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                </svg>
                <p class="text-sm text-gray-700 leading-relaxed italic mb-0 pr-6 flex-1">"{{ testimonial.content }}"</p>
                <div class="tw-flex items-center justify-between pt-3 mt-3 border-t border-gray-100">
                  <div class="tw-flex items-center gap-1.5">
                    <ActorAvatar :actor="testimonial.author_actor" :width="20"/>
                    <span class="text-xs font-medium text-gray-500">{{ testimonial.author_actor?.name }}</span>
                  </div>
                  <span class="text-[10px] text-gray-400 shrink-0">{{ formatMessageTime(testimonial.created_at) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Received -->
        <div v-if="testimonials?.received?.length > 0">
          <div class="tw-flex items-center gap-2 mb-4">
            <span class="text-xs font-semibold uppercase tracking-widest text-primary whitespace-nowrap">{{ $t('testimonials_received') }}</span>
            <div class="flex-1 h-px bg-gray-100"/>
          </div>
          <div v-for="group in testimonials.received" :key="group.target_actor?.id" class="mb-5">
            <a
              :href="'/actors/' + group.target_actor?.elastic_id"
              class="inline-flex items-center gap-2 mb-3 !no-underline group"
            >
              <ActorAvatar :actor="group.target_actor" :width="24"/>
              <span class="text-xs font-semibold text-gray-600 group-hover:text-primary transition-colors">{{ group.target_actor?.name }}</span>
            </a>
            <div class="grid md:grid-cols-2 gap-3">
              <div
                v-for="testimonial in group.testimonials"
                :key="testimonial.id"
                class="relative bg-gray-50 rounded-xl p-4 border border-gray-100 tw-flex flex-col"
              >
                <svg class="absolute top-3 right-3 h-5 w-5 text-gray-200" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                </svg>
                <p class="text-sm text-gray-700 leading-relaxed italic mb-0 pr-6 flex-1">"{{ testimonial.content }}"</p>
                <div class="tw-flex items-center justify-between pt-3 mt-3 border-t border-gray-100">
                  <div class="tw-flex items-center gap-1.5">
                    <ActorAvatar :actor="testimonial.author_actor" :width="20"/>
                    <span class="text-xs font-medium text-gray-500">{{ testimonial.author_actor?.name }}</span>
                  </div>
                  <span class="text-[10px] text-gray-400 shrink-0">{{ formatMessageTime(testimonial.created_at) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
  import OnboardingMixin from '../../util/OnboardingMixin.js'
  import Tabs from '../Tabs/Tabs.vue'
  import Avatar from '../Avatar/Avatar.vue'
  import TranslationsMixin from '../../util/TranslationsMixin.js'
  import Dropdown from '../Dropdown/Dropdown.vue'
  import UiMixin from '../../util/UiMixin.js'
  import IntroJsMixin from '../../util/IntroJsMixin.js'

  import ToggleButton from '../Form/ToggleButton.vue'
  import { fetchLatestConversation } from '@/api/conversations'
  import AvatarNew from '@/components/Avatar/AvatarNew.vue'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import { MUTATION_TYPES as FILTERS_MUTATION_TYPES } from '@/store/modules/filters'
  import MODAL_IDS from '@/constants/modal-ids.js'
  import { fetchLatestMembersForUser } from '@/api/communities'
  import { getActorWeeklyTrend, getUserPreferences } from '@/api/user'
  import { updateActor } from '@/api/actors'
  import CommunityMemberCard from '@/components/Communities/CommunityMemberCard.vue'
  import ActorMiniCard from '@/components/ActorMiniCard/ActorMiniCard.vue'
  import ChallengeCardNew from '@/components/Challenges/ChallengeCardNew.vue'
  import AnnouncementCard from '@/components/Simplified/AnnouncementCard.vue'
  import ActorInfoCard from '@/components/ActorInfo/ActorInfoCard.vue'
  import SimplifiedImageCard from '@/components/Connection/SimplifiedImageCard.vue'
  import ProfileCardsSection from '@/components/Profile/ProfileCardsSection.vue'
  import AnnouncementOrResourceCard from '@/components/Simplified/AnnouncementOrResourceCard.vue'
  import SimpleArticlePreview from '@/components/SimpleArticlePreview.vue'
  import { getImageForAnnouncement, getImageForCommunity, getImageForSpottingArea } from '@/util/helpers'
  import SimplifiedAnnouncementPreview from '@/components/Simplified/SimplifiedAnnouncementPreview.vue'
  import CommunityMixin from '@/util/CommunityMixin'
  import SpottingAreaMixin from '@/util/SpottingAreaMixin'
  import { actorLogo } from '@/util/actor'
  import { ACTION_TYPES as NOTIFICATION_ACTION_TYPES } from '@/store/modules/notifications'
  import ActorAvatar from '@/Domain/Actor/Components/ActorAvatar.vue'
  import { getUserTestimonials } from '@/Domain/Actor/Api/testimonials'
  import { fetchUserLatestCommunityAnnouncements } from '@/Domain/Announcements/Api/notifications'
  import { trackMatomoEvent } from '@/util/analytics'
  import { MATOMO_EVENT_ACTIONS, MATOMO_EVENT_CATEGORIES } from '@/constants/analytics-constants'
  import PersonalHomeCommunityCard from '@/components/Profile/PersonalHomeCommunityCard.vue'
  import PersonalHomeAnnouncementCard from '@/components/Profile/PersonalHomeAnnouncementCard.vue'
  import PersonalHomeAnnouncementModal from '@/components/Profile/PersonalHomeAnnouncementModal.vue'
  import { fetchTransitionPulseMonths } from '@/api/transition-pulse'
  import { fetchInnovationRadarQuarters } from '@/api/innovation-radar'
  import { fetchDeepDiveReports } from '@/api/deep-dive-reports'
  import TransitionReportsTrialBanner from '@/components/Dashboard/TransitionReportsTrialBanner.vue'
  import { getPortalUrl } from '@/api/transitionReportsSubscription'
  import { ACTION_TYPES as USER_ACTION_TYPES } from '@/store/modules/user.js'

  export default {
    name: 'PersonalHomeContent',
    data () {
      return {
        componentKey: 1,
        portfolios: [],
        finishedLoadingInitialData: false,
        conceptSearches: [],
        availableResources: [],
        relevantTags: [],
        acceptedTags: [],
        relevantTaxonomies: [],
        acceptedTaxonomies: [],
        updatingAllowPersonalisedContentOption: false,
        missedMessages: [],
        latestCommunityMembers: [],
        newSpottingAreaActors: [],
        challenges: [],
        announcements: [],
        activeAnnouncement: null,
        announcementModalOpen: false,
        userPreferences: {
          preferredSpottingAreaId: null,
          preferredCommunityId: null,
        },
        highlightedSection: null,
        featuredNotifications: [],
        nonFeaturedAnnouncements: [],
        actorWeeklyTrendData: {},
        testimonials: [],
        transitionPulseMonths: [],
        innovationRadarQuarters: [],
        deepDiveReports: [],
        subscriptionSuccess: false,
        portalLoading: false,
        portalError: null,
        earlyAccessBannerDismissed: localStorage.getItem('fl_early_access_dismissed') === 'true',
      }
    },
    computed: {
      hasActiveTransitionReportSubscription () {
        return this.$store.getters.hasActiveTransitionReportSubscription
      },
      hasActiveTransitionReportTrial () {
        return this.$store.getters.hasActiveTransitionReportTrial
      },
      isDeveloper () {
        return this.$store.getters.isDeveloper
      },
      canAccessReports () {
        return this.$store.getters.canAccessTransitionReports
      },
      profile () {
        return this.$store.state.user.profile
      },
      hasAnyData () {
        return this.missedMessages.length > 0 ||
          this.latestCommunityMembers.length > 0 ||
          this.newSpottingAreaActors.length > 0 ||
          this.challenges.length > 0 ||
          this.announcements.length > 0
      },
      showPreferencesGuidance () {
        // Show guidance if user hasn't set preferences and has access to communities or spotting areas
        const hasNoCommunityPreference = !this.userPreferences.preferredCommunityId && this.hasJoinedCommunities
        const hasNoSpottingAreaPreference = !this.userPreferences.preferredSpottingAreaId && this.hasAccessToSpottingAreas

        return hasNoCommunityPreference || hasNoSpottingAreaPreference
      },
      hasJoinedCommunities () {
        return this.profile && this.profile.joinedCommunityIds && this.profile.joinedCommunityIds.length > 0
      },
      hasAccessToSpottingAreas () {
        // Check if user has access to monitoring and has accessible spotting areas
        return this.$store.getters.hasAccessToMonitoring &&
          this.profile &&
          this.profile.accessibleSpottingAreas &&
          this.profile.accessibleSpottingAreas.length > 0
      },
      actorClaims () {
        if (!this.profile?.claims) return []
        return this.profile.claims.filter(c => c.actor_type !== 'Product')
      },
      productClaims () {
        if (!this.profile?.claims) return []
        return this.profile.claims.filter(c => c.actor_type === 'Product')
      },
      showAddProductButton () {
        if (this.$store.getters.isMember) return true
        return this.$store.getters.userCanCreate &&
          this.$store.getters.claimableActorTypes?.includes('Product')
      },
      showAddActorButton () {
        if (this.$store.getters.isMember) return true
        return this.$store.getters.userCanCreate &&
          this.$store.getters.claimableActorTypes?.length > 0
      },
      suggestedCommunities () {
        const joinedIds = (this.joinedCommunities || []).map(c => c.id)
        return (this.$store.state.communities.listData.data || [])
          .filter(c => !joinedIds.includes(c.id))
          .slice(0, 3)
      },
      getPreferredCommunityName () {
        let community = this.communities.filter((community) => {
          return community.id === this.getPreferredCommunityId()
        })

        if (community.length > 0) {
          return community[0].name
        }

        return null
      },
    },
    methods: {
      dismissEarlyAccessBanner () {
        this.earlyAccessBannerDismissed = true
        localStorage.setItem('fl_early_access_dismissed', 'true')
      },

      // Claimed-actor gating happens in the router (mustHaveClaimedActor guard)
      openInnovationRadar () {
        if (this.innovationRadarQuarters.length > 0) {
          this.$router.push(`/innovation-radar/${this.innovationRadarQuarters[0].id}`)
        } else {
          this.$router.push('/innovation-radar')
        }
      },
      openDeepDiveReport () {
        this.$router.push('/deep-dive-reports/balanced-protein-futures')
      },

      // T039: Open Stripe Customer Portal for direct TRP subscribers
      async openPortal () {
        this.portalLoading = true
        this.portalError = null
        try {
          const result = await getPortalUrl()
          window.location.href = result.portal_url
        } catch {
          this.portalError = this.$t('trp_portal_error')
          this.portalLoading = false
        }
      },
      actorLogo,
      getImageForCommunity,
      trackViewAll (section) {
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.PERSONAL_HOME, MATOMO_EVENT_ACTIONS.VIEW_ALL, section)
      },
      getImageForSpottingArea,
      openAnnouncementModal (announcement) {
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.PERSONAL_HOME, MATOMO_EVENT_ACTIONS.OPEN_ANNOUNCEMENT, announcement.title)
        this.activeAnnouncement = announcement
        this.announcementModalOpen = true
      },
      viewCommunity (community) {
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.PERSONAL_HOME, MATOMO_EVENT_ACTIONS.VIEW_COMMUNITY, community.name)
        this.$store.commit(FILTERS_MUTATION_TYPES.CLEAR)
        this.$store.commit(FILTERS_MUTATION_TYPES.SET_PORTFOLIO, community.portfolioId)
        this.$router.push('/communities/' + community.id)
      },
      viewSpottingArea (spottingArea) {
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.PERSONAL_HOME, MATOMO_EVENT_ACTIONS.VIEW_SPOTTING_AREA, spottingArea.name)
        this.$store.commit(FILTERS_MUTATION_TYPES.CLEAR)
        this.$router.push('/spotting-areas/' + spottingArea.id)
      },
      openWriteTestimonialModal () {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {})
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.CREATE_TESTIMONIAL)
      },
      showCreateActorModal () {
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.PERSONAL_HOME, MATOMO_EVENT_ACTIONS.ADD_ACTOR)
        if (this.$store.getters.isPublisherEnabled) {
          this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, { forceClaim: true })
        }
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ADD_ACTOR)
      },
      showAddProductModal () {
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.PERSONAL_HOME, MATOMO_EVENT_ACTIONS.ADD_PRODUCT)
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ADD_PRODUCT_FROM_PROFILE)
      },
      linkProductToParent (product) {
        const parentId = product.parentId
        if (!parentId) return

        const parentActor = this.actorClaims.find(a => a.id === parentId)
        if (!parentActor) return

        const hasProduct = [...(parentActor.has_product || []), {
          to: product.id,
          to_name: product.name,
          start: null,
          end: null,
        }]

        updateActor({ id: parentId, data: { has_product: hasProduct } })
      },
      actorBarData (actorId, actualNumber = false) {
        const trend = this.actorWeeklyTrendData[actorId]
        if (!trend) return Array(6).fill(0)
        const views = trend.dailyViews

        // Aggregate 30 daily values into 6 buckets of 5 days each
        const bucketSize = Math.ceil(views.length / 6)
        const buckets = Array.from({ length: 6 }, (_, i) => {
          const slice = views.slice(i * bucketSize, (i + 1) * bucketSize)
          return slice.reduce((a, b) => a + b, 0)
        })

        if (actualNumber) {
          return buckets
        }

        const max = Math.max(...buckets, 1)
        return buckets.map(v => Math.max(4, Math.round((v / max) * 100)))
      },
      actorTrend (actorId) {
        return this.actorWeeklyTrendData[actorId]?.trend || 'neutral'
      },
      async fetchActorWeeklyTrend () {
        try {
          this.actorWeeklyTrendData = await getActorWeeklyTrend()
        } catch (e) { /* silently ignore */
        }
      },
      async fetchUserTestimonials () {
        await getUserTestimonials()
          .then((response) => {
            this.testimonials = response
          })
      },
      async fetchTransitionPulse () {
        // TODO: replace with real API call once backend is ready
        try {
          this.transitionPulseMonths = await fetchTransitionPulseMonths()
        } catch (e) { /* silently ignore */
        }
      },
      formatPulseMonth (year, month) {
        return new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      },
      async fetchInnovationRadar () {
        const colors = ['#4f6f9f', '#5fbf7f', '#d28b4d', '#5f7f5f', '#9b59b6', '#9ca3af']

        const makeDots = (seed) => Array.from({ length: 18 }, (_, i) => ({
          x: ((seed * 7 + i * 17 + i * i * 3) % 80) + 10,
          y: ((seed * 13 + i * 11 + i * i * 5) % 80) + 10,
          color: colors[i % colors.length],
        }))
        // TODO: replace with real API call once backend is ready
        // try {
        await fetchInnovationRadarQuarters()
          .then((response) => {
            this.innovationRadarQuarters = response.map((radar, index) => {
              return {
                ...radar,
                previewDots: makeDots(index)
              }
            })
          })

        console.log(this.innovationRadarQuarters)
      },
      async fetchDeepDiveReports () {
        try {
          this.deepDiveReports = await fetchDeepDiveReports()
        } catch (e) { /* silently ignore */
        }
      },
      getImageForAnnouncement,
      async loadUserPreferences () {
        try {
          const response = await getUserPreferences()
          this.userPreferences = response.preferences
        } catch (error) {
          console.error('Error loading user preferences:', error)
        }
      },
      getPreferredCommunityId () {
        // Use preferred community if set, otherwise fall back to first available
        if (this.userPreferences.preferredCommunityId) {
          return this.userPreferences.preferredCommunityId
        }
        if (this.profile && this.profile.joinedCommunityIds && this.profile.joinedCommunityIds.length > 0) {
          return this.profile.joinedCommunityIds[0]
        }
        return null
      },
      getPreferredSpottingAreaId () {
        // Use preferred spotting area if set, otherwise fall back to first available
        if (this.userPreferences.preferredSpottingAreaId) {
          return this.userPreferences.preferredSpottingAreaId
        }
        if (this.profile && this.profile.accessibleSpottingAreas && this.profile.accessibleSpottingAreas.length > 0) {
          return this.profile.accessibleSpottingAreas[0]
        }
        return null
      },
      handleClickActorCard (actor) {
        const spottingAreaId = this.getPreferredSpottingAreaId()
        this.$router.push(`/spotting-areas/${spottingAreaId}/actors/${actor.id}`)
      },
      openConversation (conversationId, userId) {
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.PERSONAL_HOME, MATOMO_EVENT_ACTIONS.OPEN_CONVERSATION, conversationId)
        this.$router.push('/profile/conversations/' + conversationId).then(() => {
          this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
            component: 'user-information-side-panel',
            metaData: {
              userId: userId,
              allowDelete: false,
            },
          })
        })
      },
      fetchLatestConversationMessages () {
        return fetchLatestConversation()
          .then((response) => {
            this.missedMessages = response
          })
      },
      fetchLatestCommunityMembers () {
        const communityId = this.getPreferredCommunityId()
        if (communityId) {
          return fetchLatestMembersForUser()
            .then((response) => {
              this.latestCommunityMembers = response.filter((user) => {
                return user.id !== this.profile.id
              })
            })
        }
        return Promise.resolve()
      },
      fetchLatestAnnouncements () {
        const communityId = this.getPreferredCommunityId()
        if (communityId) {
          return fetchUserLatestCommunityAnnouncements()
            .then((response) => {
              this.announcements = response
            })
            .catch((error) => {
              console.error('Error fetching announcements:', error)
            })
        }
      },
      formatMessageTime (timestamp) {
        return new Date(timestamp).toLocaleDateString()
      },
      async resetUserObjects () {
        // Refresh data when user profile is updated
        await this.loadUserPreferences()
        await this.fetchLatestCommunityMembers()
        await this.fetchLatestAnnouncements()
      },
    },
    created () {
      this.$bus.on('updateUserProfile', this.resetUserObjects)
      this.$bus.on('productAddedViaOverlay', this.linkProductToParent)
    },
    async mounted () {
      // T025: Handle subscription success redirect from Stripe
      if (this.$route.query.subscription === 'success') {
        this.subscriptionSuccess = true
        this.$router.replace({ query: {} })
        this.$store.dispatch(USER_ACTION_TYPES.REFRESH_PROFILE)
      }

      await this.loadUserPreferences()
      this.fetchLatestConversationMessages()
      await this.fetchLatestCommunityMembers()
      this.fetchLatestAnnouncements()
      await this.fetchUserTestimonials()
      this.finishedLoadingInitialData = true
      this.fetchTransitionPulse()
      this.fetchInnovationRadar()
      this.fetchDeepDiveReports()
      await this.fetchActorWeeklyTrend()
      await this.$store.dispatch(NOTIFICATION_ACTION_TYPES.FETCH_NOTIFICATIONS_LIST, {
        limit: this.$store.state.notifications.listData.fetchLimit,
        offset: 0,
      })
    },
    beforeUnmount () {
      this.$bus.off('updateUserProfile', this.resetUserObjects)
      this.$bus.off('productAddedViaOverlay', this.linkProductToParent)
    },
    mixins: [OnboardingMixin, TranslationsMixin, UiMixin, IntroJsMixin, CommunityMixin, SpottingAreaMixin],
    components: {
      TransitionReportsTrialBanner,
      PersonalHomeCommunityCard,
      PersonalHomeAnnouncementCard,
      PersonalHomeAnnouncementModal,
      ActorAvatar,
      SimplifiedAnnouncementPreview,
      SimpleArticlePreview,
      AnnouncementOrResourceCard,
      ProfileCardsSection,
      SimplifiedImageCard,
      ActorInfoCard,
      AnnouncementCard,
      ChallengeCardNew,
      ActorMiniCard,
      CommunityMemberCard,
      AvatarNew,
      Tabs,
      Avatar,
      Dropdown,
      ToggleButton
    },
  }
</script>

<style lang="scss" scoped>
  @import "../../../scss/_variables.scss";

  /* ── TRP card animations ─────────────────────────────── */

  // Sparkline draw
  .ph-sparkline {
    .ph-spark-path {
      stroke-dasharray: 155;
      stroke-dashoffset: 155;
      animation: ph-spark-draw 2.6s ease-in-out infinite;
    }
  }

  @keyframes ph-spark-draw {
    0% {
      stroke-dashoffset: 155;
      opacity: 0.15;
    }
    8% {
      opacity: 0.8;
    }
    65% {
      stroke-dashoffset: 0;
      opacity: 0.8;
    }
    82% {
      stroke-dashoffset: 0;
      opacity: 0.15;
    }
    100% {
      stroke-dashoffset: 155;
      opacity: 0.15;
    }
  }

  // Radar sweep + blinking dots
  .ph-radar-sweep {
    background: conic-gradient(
      from 0deg,
      rgba(92, 204, 97, 0) 0%,
      rgba(92, 204, 97, 0.22) 45deg,
      rgba(92, 204, 97, 0) 90deg
    );
    animation: ph-radar-rotate 3s linear infinite;
  }

  @keyframes ph-radar-rotate {
    to {
      transform: rotate(360deg);
    }
  }

  .ph-radar-dot {
    position: absolute;
    width: 5px;
    height: 5px;
    background: #5ccc61;
    border-radius: 50%;
    animation: ph-dot-blink 3s ease-in-out infinite;

    &.ph-radar-dot--2 {
      animation-delay: 1.1s;
      animation-duration: 2.7s;
    }

    &.ph-radar-dot--3 {
      animation-delay: 2.2s;
      animation-duration: 3.4s;
    }
  }

  @keyframes ph-dot-blink {
    0%, 100% {
      opacity: 0;
      transform: scale(0.3);
    }
    25%, 70% {
      opacity: 0.9;
      transform: scale(1);
    }
  }

  // Deep Dive stacked pages fan
  .ph-deepdive-back {
    transform: rotate(-6deg);
    animation: ph-page-fan 2.8s ease-in-out infinite;
    transform-origin: bottom center;
  }

  .ph-deepdive-front {
    animation: ph-page-lift 2.8s ease-in-out infinite;
    animation-delay: 0.1s;
  }

  @keyframes ph-page-fan {
    0%, 100% {
      transform: rotate(-6deg) translateY(0);
    }
    45% {
      transform: rotate(-16deg) translateY(-3px);
    }
  }

  @keyframes ph-page-lift {
    0%, 100% {
      transform: translateY(0);
    }
    45% {
      transform: translateY(-3px);
    }
  }

  /* ── Early Access Banner ──────────────────────────────── */
  .ph-early-access-banner {
    background-color: #f4f9f1;
    border: 1px solid #dcf0d8;
  }

  .ph-ea-badge {
    display: inline-block;
    font-size: 0.625rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #2d6a1f;
    background-color: #dcf0d8;
    padding: 2px 8px;
    border-radius: 4px;
  }

  .ph-ea-bottom-bar {
    background-color: rgba(255, 255, 255, 0.5);
  }

  .ph-ea-btn-feedback {
    border: 1px solid #2d6a1f;
    color: #2d6a1f;

    &:hover {
      background-color: #f4f9f1;
    }
  }

  .ph-ea-btn-learn {
    border: 1px solid #e2e8f0;
    color: #475569;

    &:hover {
      background-color: #f8fafc;
    }
  }

  /* Primary-coloured link used for "View all" buttons */
  .ph-link-primary {
    color: var(--primary, #2563eb);

    :deep(svg) {
      flex-shrink: 0;
      transition: transform 0.2s ease;
    }

    &:hover :deep(svg) {
      transform: translateX(3px);
    }
  }

  /* ── Rail (horizontal scroll on small screens) ────────────── */
  .ph-communities-rail {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 0.75rem;

    &--suggested {
      grid-template-columns: 1fr 1fr 1fr;
    }
  }

  @media (max-width: 480px) {
    .ph-communities-rail {
      grid-template-columns: 1fr;
    }
  }

  /* ── Empty state icon wrap (animations + :deep override) ─── */
  .ph-empty-state__icon-wrap {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.85rem;
    color: #9ca3af;

    :deep(.svg-icon) {
      display: block;
      width: 22px;
      height: 22px;
      fill: #9ca3af;
    }

    &--pulse {
      animation: ph-fade-up 0.4s ease both, ph-icon-pulse 3s ease-in-out 0.6s infinite;
    }

    &--float {
      animation: ph-fade-up 0.4s ease both, ph-icon-float 4s ease-in-out 0.6s infinite;
    }

    &--bounce {
      animation: ph-fade-up 0.4s ease both, ph-icon-bounce 2.5s ease-in-out 0.6s infinite;
    }
  }

  /* Fade-up entrance animation for empty state containers/text */
  .ph-empty-anim {
    animation: ph-fade-up 0.4s ease both;
  }

  .ph-empty-anim-text {
    animation: ph-fade-up 0.4s 0.1s ease both;
  }

  /* ── Keyframes ────────────────────────────────────────────── */
  @keyframes ph-fade-up {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes ph-icon-pulse {
    0%, 100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(99, 102, 241, 0);
    }
    50% {
      transform: scale(1.06);
      box-shadow: 0 0 0 6px rgba(99, 102, 241, 0.08);
    }
  }

  @keyframes ph-icon-float {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-5px);
    }
  }

  @keyframes ph-icon-bounce {
    0%, 100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-6px);
    }
    60% {
      transform: translateY(-3px);
    }
  }

  /* ── Founding Member CTA Banner ─────────────────────────── */
  .ph-founding-cta-banner {
    background-color: #f4f9f1;
    border: 1px solid #dcf0d8;
  }

  .ph-fcta-badge {
    display: inline-block;
    font-size: 0.625rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #2d6a1f;
    background-color: #dcf0d8;
    padding: 2px 8px;
    border-radius: 4px;
  }

  .ph-fcta-right {
    background-color: rgba(255, 255, 255, 0.5);
    border-left: 1px solid #dcf0d8;
  }

  .ph-fcta-btn-primary {
    background-color: #2d6a1f;
    color: #fff;

    &:hover {
      background-color: #245818;
    }
  }

  .ph-fcta-btn-secondary {
    color: #475569;
    border: 1px solid #e2e8f0;

    &:hover {
      background-color: #f8fafc;
    }
  }


</style>
